import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import OsScroll from '../ui/OsScroll';

/**
 * The anchored dropdown shell for the invitee picker's SUGGESTIONS (owner,
 * 2026-08-31: "the People you chat with should show only on search focus, as a
 * dropdown part of the search and not just show by default").
 *
 * Shape copied from `DropdownList.js`, the event editor's existing dropdown
 * shell, rather than invented: portal to `document.body`, `cal-popover` chrome
 * (`position: fixed; z-index: 1000` — above the modal root's `z-50`, and with a
 * dark-mode rule already), anchored to the trigger's measured rect, `OsScroll`
 * for the list, and an outside-mousedown close.
 *
 * WHY A PORTAL AND NOT `position: absolute` INSIDE THE FORM. An absolutely
 * positioned dropdown lives or dies by its ancestor chain: one `overflow`
 * anywhere between here and `<body>` clips it, and one stacking context buries
 * it — and jsdom renders no layout, so no test in this repo can see either
 * failure. The portal removes the question instead of answering it, and it is
 * the answer the two OTHER dropdowns in this very modal (`TimeCombobox`,
 * `EditorSelect`, both through `DropdownList`) already ship in production.
 *
 * `DropdownList` itself could not be reused: its rows are `{key, label, hint}`
 * with no avatar, no second line, no disabled state and no section headings.
 * Widening it would have destabilised the two shipped controls that depend on
 * it, so this is a sibling shell, not a fork of the rows.
 *
 * Presentation only. The OWNER of the trigger decides when it opens and closes
 * and what a pick means — same contract as `DropdownList`.
 *
 * `data-invitee-dropdown` is deliberately NOT `data-editor-dropdown`: the two
 * attributes make the two dropdown families close each OTHER. Sharing one
 * marker would let a click on a time option leave this list standing open.
 */
export default function InviteeSuggestionsDropdown({
  anchorRect,
  id,
  ariaLabel,
  onRequestClose,
  maxHeight = 260,
  children,
}) {
  // Outside mousedown closes. This is NOT redundant with the picker's own
  // focus-out handler: a mousedown that calls `preventDefault()` (every other
  // portal dropdown in this editor does) never moves focus, so no blur fires
  // and only this listener can see it.
  useEffect(() => {
    const onDown = (e) => {
      if (e.target.closest?.('[data-invitee-dropdown]')) return;
      onRequestClose?.();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onRequestClose]);

  if (!anchorRect) return null;

  // NEVER overlap the anchor. The previous version clamped `top` upward with a
  // `Math.min` so the panel would fit above the viewport bottom — which, for an
  // input low in the modal, pulled the list up ON TOP of the box you are typing
  // in (owner saw exactly this). Overlapping the trigger is the worst of the
  // three options, so: sit below when there is room, otherwise flip above, and
  // if neither side is comfortable take the roomier one and let the list scroll
  // inside whatever space that side actually has.
  const GAP = 2;
  const EDGE = 8;
  const vh = window.innerHeight || 800;
  const roomBelow = vh - anchorRect.bottom - GAP - EDGE;
  const roomAbove = anchorRect.top - GAP - EDGE;
  const flip = roomBelow < Math.min(maxHeight, 160) && roomAbove > roomBelow;
  const room = Math.max(80, Math.min(maxHeight, flip ? roomAbove : roomBelow));
  const placement = flip
    ? { top: Math.max(EDGE, anchorRect.top - GAP - room), maxHeight: room }
    : { top: anchorRect.bottom + GAP, maxHeight: room };

  return ReactDOM.createPortal(
    <div
      className="cal-popover"
      data-invitee-dropdown="true"
      id={id}
      role="listbox"
      aria-label={ariaLabel}
      // THE BLUR-VS-CLICK TRAP, solved once for the whole surface. A row's
      // `onClick` fires AFTER the input's `blur`, so anything that closes on
      // blur unmounts the row before the click lands and the person is never
      // added. Suppressing the default action of `mousedown` stops the browser
      // moving focus at all: the input never blurs, the row is still mounted
      // when `click` fires, and focus stays in the search box so the next
      // person can be picked without re-focusing (owner decision 3).
      onMouseDown={(e) => e.preventDefault()}
      style={{
        top: placement.top,
        left: anchorRect.left,
        width: Math.max(anchorRect.width, 260),
        padding: 6,
      }}
    >
      <OsScroll style={{ maxHeight: placement.maxHeight }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>
      </OsScroll>
    </div>,
    document.body
  );
}
